import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { T, TF } from "react-translator-component";
import { useAppDispatch } from "../../redux/store";
import { clearGroupInfo } from "../../redux/Reducers/gcGroupReducer";
import appServices from "../../services/app-services/appServices";
import { isClosedGroup, isGroupLeader, isRegularAppUser, isSubLeaderRole } from "../../util/utils";
import { createUpdatingPostState } from "../../util/CreatePost"
import LoadingModal from "../LoadingModal";
import PostAttachment from '../PostAttachment'
import PostTags from "../PostTags";
import MessageModal from "../Models/GreenCity/MessageModal";
import { colors } from "../../common/colors";
import { Box, Button, Typography } from "@mui/material";
import Separator from "../Separator";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SafeTextWithLinks from "../SafeTextWithLinks";

interface Props {
  item: any;
}

const EventPostDetailSeeMore = ({ item }: Props) => {
  const detail = item?.post_events[0];
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: any) => state.userAuth);
  const [loading, setLoading] = useState<boolean>(false);
  const [userPaymentDetail, setUserPaymentDetail] = useState<any>({});
  const [openModal, setOpenModal] = useState<boolean>(false);

  const today = dayjs().startOf("day"); // Midnight of today
  const eventEnd = dayjs(detail?.end_date).startOf("day"); // Normalize end_date to midnight
  const EventDatePassed =
    eventEnd.isSame(today, "day") || eventEnd.isAfter(today);

  const isMember = (): boolean => {
    if (isClosedGroup(item.member_group.closed_at)) return false;

    const { member_group_users } = item.member_group;

    // Check if the user is a member
    const isUserMember = member_group_users?.some(
      (member: any) => member?.id === user?.id && member?.status === "approved"
    );

    // Return true if the user is either a member or the owner
    return isUserMember || isGroupLeader(item.member_group, user);
  };

  const getPaymentDetails = async () => {
    try {
      setLoading(true);
      const res = await appServices.searchEventParticipant(
        detail?.id,
        user?.id
      );

      setUserPaymentDetail(res);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  useEffect(() => {
    if (!detail) return
    getPaymentDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  const navigateToPaymentDetails = () => {
    navigate("/usereventcancellation", {
      state: {
        postEventUserId: userPaymentDetail.id,
      },
    });
  };

  const renderDescription = () => {
    const formatEventDate = (start_date: any, end_date: any) => {
      const startDate = dayjs(start_date);
      const endDate = dayjs(end_date);

      if (startDate.isSame(endDate, "day")) {
        // If start and end dates are the same
        return startDate.format("YYYY年M月D日(ddd)");
      } else {
        // If start and end dates are different
        return `${startDate.format("YYYY年M月D日(ddd)")}~${endDate.format(
          "M月D日(ddd)"
        )}`;
      }
    };

    const isRoleAllowedToEdit = (item: any) => {
      const subLeader = item?.member_group?.member_group_users?.filter((item: { group_role: string }) => isSubLeaderRole(item.group_role))
      return (
        isGroupLeader(item?.member_group, user) ||
        subLeader?.some((item: { id: number }) => item.id === user?.id)
      )
    }

    const isAlreadyApplied = () => {
      const paymentStatus = userPaymentDetail?.status
      return paymentStatus === "applied" || paymentStatus === "Cancel-pending" || paymentStatus === "confirmed"
    }

    const isEditable = () => {
      return isRoleAllowedToEdit(item) && !isClosedGroup(item?.member_group?.closed_at)
    }

    const handleSubmit = () => {
      if (!isRegularAppUser(user)) return

      if (isEditable()) {
        navigate("/createPost", createUpdatingPostState(item, user, item.member_group, dispatch))
      } else if (isAlreadyApplied()) {
        navigateToPaymentDetails()
      } else {
        if (!EventDatePassed) return

        if (isMember()) {
          navigate("/usereventpaymentdetail", {state: {postId: item.id}})
        } else {
          setOpenModal(true);
        }
      }
    }

    const closeMessageModal = () => {
      dispatch(clearGroupInfo());
      setOpenModal(false);
      navigate("/groupinforeview", {state: {groupId: item?.member_group?.id}});
    }

    return (
      <div
        style={{
          fontSize: 14,
          marginLeft: 6,
          marginRight: 6,
          lineBreak: "anywhere",
        }}
      >
        <LoadingModal visible={loading} handleClose={() => {}} />
        <MessageModal
          buttonText={T("event_post_detail_see_to_group_top")}
          buttonStyles={style.messageModalButton}
          open={openModal}
          message={
            `${T("event_post_detail_see_only_group_member")}`
          }
          showButton
          onContinue={() => {}}
          onClose={() => closeMessageModal()}
          alignLeft
        />

        <SafeTextWithLinks text={detail?.description} />

        <div style={{ margin: "10px 10px 0 10px", }}>
          <PostTags tags={item?.post_tags || []} />
        </div>

        <Box sx={{ marginTop: 2, padding: "0 6px" }}>
          <Box>
            <Typography sx={{ textAlign: "left" }}>
              {TF(T("parentheses_with_corner"), T("event_location"), "")}
            </Typography>
            <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
            <Typography sx={{ textAlign: "left" }}>
              {detail?.venue_name}
            </Typography>
            <Typography sx={{ textAlign: "left" }}>
              {detail?.postal_code}
            </Typography>
            <Typography sx={{ textAlign: "left" }}>
              {detail?.address}
            </Typography>
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <Typography sx={{ textAlign: "left" }}>
              {TF(T("parentheses_with_corner"), T("url"), "")}
            </Typography>
            <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
            <div>
              <SafeTextWithLinks text={detail?.url} />
            </div>
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <Typography sx={{ textAlign: "left" }}>
              {TF(T("parentheses_with_corner"), T("files"), "")}
            </Typography>
            <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
            {item?.attachments?.map((attachment: any, index: number) => (
              <div key={index} style={style.filesBox}>
                <PostAttachment
                  post_id={item.id}
                  attachment={attachment}
                />
              </div>
            ))}
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <Typography sx={{ textAlign: "left" }}>
              {TF(T("parentheses_with_corner"), T("event_application_application_term"), "")}
            </Typography>
            <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
            <Typography sx={{ marginTop: 1 }}>
              {detail?.participant_settings.length > 0 && (
                formatEventDate(
                  detail?.application_start_date,
                  detail?.application_end_date
                )
              )}
            </Typography>
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <Typography sx={{ textAlign: "left" }}>
              {TF(T("parentheses_with_corner"), T("event_participant_setting"), "")}
            </Typography>
            <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
            {detail?.participant_settings?.map((item: any, index: any) => {
              return (
                <Box key={index}>
                  <Typography>{TF(T("event_post_detail_see_subject"), item?.name)}</Typography>
                  <Typography>
                    {TF(T("event_post_detail_see_capacity"), item?.number_of_participants)}
                  </Typography>
                  {item?.free ? (
                    <Typography>{TF(T("event_post_detail_see_fee"), T("free"))}</Typography>
                  ) : (
                    <Typography>
                      {TF(T("event_post_detail_see_fee"), `${Number(item?.fee_amount)}${T("unit_yen")}`)}
                    </Typography>
                  )}
                  <Separator
                    color={colors.farm_green500}
                    sx={{ height: "2px" }}
                  />
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              marginTop: 4,
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >

            <Box>
              <Typography sx={{ marginTop: 2 }}>
                {TF(T("parentheses_with_corner"), T("qualification"), "")}
              </Typography>
              <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
              <Typography>
                {detail?.member_participation_rule === "誰でも参加できます。"
                  ? T("event_post_detail_see_group_member_can_join")
                  : detail?.member_participation_rule}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ marginTop: 2 }}>
                {TF(T("parentheses_with_corner"), T("about_cancel"), "")}
              </Typography>
              <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
              <Typography>{detail?.application_cancellation_rule}</Typography>
            </Box>

            <Box>
              <Typography sx={{ marginTop: 2 }}>
                {T("event_post_detail_see_payment_method_is_mebuku_pay")}
              </Typography>
              <Separator color={colors.farm_green500} sx={{ height: "2px" }} />
            </Box>

            {item?.expiry_date && (
              <Box>
                <Typography sx={{ marginTop: 2 }}>
                  {TF(T("event_post_detail_see_expiry_date"), item?.expiry_date)}
                </Typography>
                <Separator
                  color={colors.farm_green500}
                  sx={{ height: "2px" }}
                />
              </Box>
            )}
          </Box>

          {(isEditable() || isAlreadyApplied() || (EventDatePassed && detail?.participant_settings.length > 0)) && (
            <Box>
              <Button
                disabled={!isRegularAppUser(user)}
                color="secondary"
                sx={style.submitButton}
                size={"large"}
                variant="outlined"
                onClick={() => handleSubmit()}
                endIcon={
                  <ArrowForwardIosIcon
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      margin: "10px",
                    }}
                  />
                }
              >
                {isEditable() ?
                  T('event_post_detail_see_edit_cancel') :
                  isAlreadyApplied() ?
                    T('view_detail') :
                    T('apply')
                }
              </Button>
            </Box>
          )}
        </Box>
      </div>
    );
  };

  return <div className="post">{renderDescription()}</div>;
};
const style = {
  optionButton: {
    border: "1px solid #4792E0",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "3px",
  },
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    marginTop: "20px",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
  },
  filesBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    gap: "25px",
    marginTop: "10px",
    padding: "5px 0",
  },
  linkText: {
    userSelect: "text" as "text",
    fontSize: 16,
    fontFamily: "mplus-1c-regular",
    wordWrap: "break-word" as "break-word",
  },
  messageModalButton: {
    color: colors.farm_blue200,
    border: "2px solid ",
    margin: "0px 30px",
    borderRadius: "10px",
    padding: "10px",
    width: "85%",
    maxWidth: 300,
  }
};

export default EventPostDetailSeeMore;
